import React from 'react';

const ComputeOverviewDiagram = () => {
  // Color scheme: cream with black outlines
  const colors = {
    background: '#FFFEF8',  // Cream background
    boxFill: '#FFFEF8',     // Cream box fill
    boxBorder: '#333333',   // Light black outline
    text: '#000000',        // Black text
    circle1: '#005000',
    circle2: '#111111',
    circle3: '#888888',
    circle4: '#cccccc',
    circle5: '#000000',
    hoverColor: '#f0f0e0',  // Slightly darker cream for hover effect
  };

  // URLs for each section (replace these with your actual heading URLs)
  const sectionUrls = {
    section1: '#section-1-compute-production',
    section2: '#section-2-compute-distribution',
    section3: '#section-3-compute-usage',
    section4: '#section-4-ai-research-automation',
    section5: '#section-5-industry-metrics',
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    backgroundColor: colors.background,
    padding: '2px',
    width: '100%',
    height: '465px', // Use full height
    fontFamily: '"Courier New", Courier, monospace',  // Clearer monospace font
    color: colors.text,
    borderRadius: '1px',
    overflowX: 'auto',
    maxWidth: '100%',
  };

  const columnStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '2px',
    position: 'relative',
    height: '100%',
  };

  // Base section box style
  const baseSectionBox: React.CSSProperties = {
    flex: 1,
    backgroundColor: colors.boxFill,
    border: `2px solid ${colors.boxBorder}`,
    borderRadius: '6px',
    padding: '2px',
    marginBottom: '2px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    height: '100%',
    cursor: 'pointer',  // Show pointer cursor on hover
    transition: 'background-color 0.3s ease', // Smooth transition for hover effect
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '6px',
    textAlign: 'center',
    fontFamily: '"Courier New", Courier, monospace',
  };
  const title2Style: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '6px',
    textAlign: 'center',
    fontFamily: '"Courier New", Courier, monospace',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontStyle: 'italic',
    marginBottom: '20px',
    textAlign: 'center',
    fontFamily: '"Courier New", Courier, monospace',
  };

  const subBoxStyle: React.CSSProperties = {
    backgroundColor: colors.boxFill,
    border: `2px solid ${colors.boxBorder}`,
    borderRadius: '6px',
    padding: '12px',
    width: '100%',
    height: '160px',  // Made taller to fit the circle better
    marginTop: 'auto',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  };

  const circleContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    position: 'relative',
  };

  // Hover state management for section boxes
  const [hoveredSection, setHoveredSection] = React.useState<string | null>(null);

  // Circle component with label
  const Circle = ({ 
    label, 
    fill, 
    size = 60, 
    top, 
    left 
  }: { 
    label: string; 
    fill: string; 
    size?: number; 
    top?: string; 
    left?: string; 
  }) => {
    return (
      <div style={{ 
        position: 'absolute',
        top: top || 'auto',
        left: left || 'auto',
        width: size, 
        height: size + 24, 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'none', // Make circles not interfere with clicks/hovers
      } as React.CSSProperties}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          <circle 
            cx={size/2} 
            cy={size/2} 
            r={size/2 - 2} 
            fill={fill} 
            stroke={colors.boxBorder}
            strokeWidth="1"
          />
        </svg>
        <div style={{ 
          fontSize: '12px', 
          marginTop: '4px', 
          textAlign: 'center', 
          fontFamily: '"Courier New", Courier, monospace',
          maxWidth: size + 20,
          color: colors.text,
          fontWeight: 'bold',
        } as React.CSSProperties}>
          {label}
        </div>
      </div>
    );
  };

  // Function to handle click on a section
  const handleSectionClick = (sectionKey: string) => {
    window.location.href = sectionUrls[sectionKey as keyof typeof sectionUrls];
  };

  return (
    <div style={containerStyle}>
      {/* Section 1: Compute Production */}
      <div style={columnStyle}>
        <div 
          style={{
            ...baseSectionBox, 
            minHeight: '460px',
            backgroundColor: hoveredSection === 'section1' ? colors.hoverColor : colors.boxFill,
          }}
          onMouseEnter={() => setHoveredSection('section1')}
          onMouseLeave={() => setHoveredSection(null)}
          onClick={() => handleSectionClick('section1')}
        >
          <div style={titleStyle}>Section 1: Compute Production</div>
          <div style={subtitleStyle}>How much compute will be produced?</div>
          
          <div style={circleContainerStyle}>
            {/* One large circle for production pipeline */}
            <Circle 
              label="Compute Production" 
              fill={colors.circle1} 
              size={120}
              top="20%"
              left="calc(50% - 60px)"
            />
          </div>
        </div>
      </div>
      
      {/* Section 2: Compute Share */}
      <div style={columnStyle}>
        <div 
          style={{
            ...baseSectionBox, 
            minHeight: '460px',
            backgroundColor: hoveredSection === 'section2' ? colors.hoverColor : colors.boxFill,
          }}
          onMouseEnter={() => setHoveredSection('section2')}
          onMouseLeave={() => setHoveredSection(null)}
          onClick={() => handleSectionClick('section2')}
        >
          <div style={titleStyle}>Section 2: Compute Distribution</div>
          <div style={subtitleStyle}>Who will the compute go to?</div>
          
          <div style={circleContainerStyle}>
            {/* Multiple circles for different companies */}
            <Circle 
              label="OpenAI" 
              fill={colors.circle1} 
              size={70}
              top="-5%"
              left="calc(50% - 35px)"
            />
            <Circle 
              label="Google" 
              fill={colors.circle2} 
              size={60}
              top="26%"
              left="calc(50% - 30px)"
            />
            <Circle 
              label="Anthropic" 
              fill={colors.circle3} 
              size={50}
              top="55%"
              left="calc(50% - 25px)"
            />
            <Circle 
              label="Others" 
              fill={colors.circle4} 
              size={40}
              top="78%"
              left="calc(50% - 20px)"
            />
          </div>
        </div>
      </div>
      
      {/* Section 3: Compute Usage with Section 4 as sub-box */}
      <div style={columnStyle}>
        <div 
          style={{
            ...baseSectionBox, 
            minHeight: '460px', 
            position: 'relative',
            backgroundColor: hoveredSection === 'section3' ? colors.hoverColor : colors.boxFill,
          }}
          onMouseEnter={() => setHoveredSection('section3')}
          onMouseLeave={() => setHoveredSection(null)}
          onClick={() => handleSectionClick('section3')}
        >
          <div style={titleStyle}>Section 3: Compute Usage</div>
          <div style={subtitleStyle}>What will they use it on?</div>
          
          <div style={circleContainerStyle}>
            {/* Circles for different usage types */}
            <Circle 
              label="Training" 
              fill={colors.circle1} 
              size={40}
              top="-10%"
              left="calc(50% - 20px)"
            />
            <Circle 
              label="External Deployment" 
              fill={colors.circle2} 
              size={50}
              top="24%"
              left="calc(50% - 25px)"
            />
            <Circle 
              label="Internal" 
              fill={colors.circle3} 
              size={40}
              top="66%"
              left="calc(50% - 20px)"
            />
          </div>
          
          {/* Section 4: Inference Compute Special (Sub-box) */}
          <div 
            style={{
              ...subBoxStyle,
              backgroundColor: hoveredSection === 'section4' ? colors.hoverColor : colors.boxFill,
            }}
            onMouseEnter={(e) => {
              e.stopPropagation();  // Prevent triggering parent hover
              setHoveredSection('section4');
            }}
            onMouseLeave={() => setHoveredSection(null)}
            onClick={(e) => {
              e.stopPropagation();  // Prevent triggering parent click
              handleSectionClick('section4');
            }}
          >
            <div style={title2Style}>Section 4: Inference Compute</div>
            <div style={{ position: 'relative', height: '60px' } as React.CSSProperties}>
              <Circle 
                label="Internal Agents" 
                fill={colors.circle4} 
                size={40}
                top="-5px"
                left="calc(50% - 20px)"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Section 5: Industry Metrics */}
      <div style={columnStyle}>
        <div 
          style={{
            ...baseSectionBox, 
            minHeight: '460px',
            backgroundColor: hoveredSection === 'section5' ? colors.hoverColor : colors.boxFill,
          }}
          onMouseEnter={() => setHoveredSection('section5')}
          onMouseLeave={() => setHoveredSection(null)}
          onClick={() => handleSectionClick('section5')}
        >
          <div style={titleStyle}>Section 5: Industry Metrics</div>
          
          <div style={circleContainerStyle}>
            {/* Circles for different metrics */}
            <Circle 
              label="Revenue" 
              fill={colors.circle1} 
              size={70}
              top="5%"
              left="calc(50% - 35px)"
            />
            <Circle 
              label="Costs" 
              fill={colors.circle2} 
              size={70}
              top="35%"
              left="calc(50% - 35px)"
            />
            <Circle 
              label="Power" 
              fill={colors.circle3} 
              size={50}
              top="65%"
              left="calc(50% - 25px)"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComputeOverviewDiagram;